import type {ReactElement} from 'react';
import Tooltip from 'react-bootstrap/Tooltip';
import {Text, TextSize} from '@Components/text';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {formatNumber} from '@Utils/number.util';
import type {OverlayInjectedProps} from 'react-bootstrap/Overlay';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {ErrorType} from '@Components/ai-prompt-box/components/ai-prompt-box-issue/ai-prompt-box-issue.types';
import {CrownUpsellBadge} from '@Components/crown-upsell-badge/crown-upsell-badge';
import {CreditsPillSize, CreditsPillTheme} from '@Components/ai-credits-pill/ai-credits-pill.types';
import {AiCreditsPill} from '@Components/ai-credits-pill/ai-credits-pill';

interface CreditsPillProps {
  id: string;
}

export function AiPromptBoxCreditsPill({...props}: CreditsPillProps): ReactElement | null {
  const {state} = useAiPromptBox(props.id);
  const actionCost = state.creditsPill?.actionCost ?? 0;
  const remainingCredits = state.creditsPill?.remainingCredits ?? 0;
  const showPill = state.creditsPill?.visible ?? false;
  const showPremiumIcon = state.creditsPill?.showPremiumIcon ?? false;

  const getTheme = (): CreditsPillTheme => {
    if (state.error?.type === ErrorType.WARNING) {
      return CreditsPillTheme.WARNING;
    }

    if (remainingCredits < 1) {
      return CreditsPillTheme.ERROR;
    }

    if (remainingCredits < actionCost) {
      return CreditsPillTheme.WARNING;
    }

    return CreditsPillTheme.DEFAULT;
  };

  const getTooltipContent = (): ReactElement => {
    return (
      <div className="flex-center flex-v-row">
        <Text
          dangerouslySetInnerHTML
          size={TextSize.XXSMALL}
          val={actionCost <= 1 ? window.i18next.t('pmwjs_will_cost_1_credit') : window.i18next.t('pmwjs_will_cost_x_credit', {creditsCost: actionCost})}
        />
        <Text size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_curently_credit_credits', {credit: formatNumber(remainingCredits)})} />
      </div>
    );
  };

  const getCreditsPillTooltip = (): ReactElement<OverlayInjectedProps, string> => {
    return <Tooltip className={`spacing-m-b-2`}>{getTooltipContent()}</Tooltip>;
  };

  const getPill = (): ReactElement => {
    return (
      <div>
        <AiCreditsPill credits={actionCost} theme={getTheme()} size={CreditsPillSize.SMALL} />
      </div>
    );
  };

  const getCreditsPill = (): ReactElement | null => {
    if (!showPill) {
      return null;
    }

    if (showPremiumIcon) {
      return <CrownUpsellBadge />;
    }

    return (
      <OverlayTrigger
        trigger={['hover', 'focus', 'click']}
        overlay={getCreditsPillTooltip()}
        delay={{show: 200, hide: 200}}
        placement="top"
        show={showPill ? undefined : false}
        popperConfig={{
          modifiers: [
            {
              name: 'zIndexFix',
              enabled: true,
              phase: 'write' as const,
              fn: ({state}: {state: {elements: {popper: HTMLElement}}}): void => {
                state.elements.popper.style.zIndex = '5000';
              },
            },
          ],
        }}
      >
        {getPill()}
      </OverlayTrigger>
    );
  };

  return getCreditsPill();
}
